import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Mic, Bot, Zap, Building2, Mail, Archive, MessageSquare,
  Clock, RotateCcw, Play,
  ChevronRight, ChevronDown, FileText, RefreshCw, Link2, Loader2,
} from 'lucide-react'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useThreadMessages } from '@/hooks/use-inbox'
import type { Thread, Kind } from './types'
import { ThreadActionsMenu } from './InboxFeed'
import { WorkspaceAccentLabel } from './workspace-accent'
import { testIds } from '@/lib/test-ids'
import { AgentDispatchPanel } from '@/components/features/inbox/agent-dispatch-panel'
import { useAuth } from '@/providers/auth-provider'
import { useWorkspace } from '@/providers/workspace-provider'
import { useNeedsPassword } from '@/hooks/use-needs-password'
import { fetchWithAuth } from '@/lib/api'
import { openAccountPasswordSettings } from '@/lib/open-settings'
import { useChatSurfaceRegistryOptional } from '@/hooks/use-chat-surface-registry'
import { openInboxThreadInChat } from '@/lib/open-inbox-thread-in-chat'
import { ADD_TO_CURRENT_CHAT_LABEL, AddToCurrentChatIcon } from '@/lib/add-to-current-chat'
import { dispatchInboxThreadAddToChat } from '@/lib/inbox-thread-share'
import { MarkdownProse } from '@/components/features/shared/markdown-prose'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { IndexPreviewEmpty } from '@/components/integrations/_shared/index-preview-empty'
import { useRegisterInboxThreadChromeActions } from '@/components/features/inbox/inbox-thread-chrome-context'
import { getConversation, type AIMessage } from '@/lib/api/conversations'
import { getThread } from '@/lib/api/threads'
import {
  SoulProposalActionBar,
  getSoulProposalContextDoc,
  isSoulProposalThread,
  isUnresolvedSoulProposal,
} from '@/components/features/inbox/SoulProposalActionBar'
import { SoulProposalDecisionEvent } from '@/components/features/inbox/SoulProposalDecisionEvent'
import { InboxDocumentEvent } from '@/components/features/inbox/InboxDocumentEvent'
import type { ThreadMessage } from '@/hooks/use-inbox'

function isDocumentEventMessage(msg: ThreadMessage): boolean {
  const kind = (msg.metadata as { kind?: string } | undefined)?.kind
  return kind === 'document_event'
}

/** Panel-density markdown for inbox / flow bodies (matches document secondary preview). */
function InboxMarkdownBody({ body }: { body: string }) {
  return (
    <MarkdownProse
      content={body}
      linkMode="inbox"
      density="panel"
      testId={testIds.inbox.readerMarkdown}
    />
  )
}

function workspaceInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  if (parts[0]?.length) return parts[0]!.slice(0, 2).toUpperCase()
  return '?'
}

function parseInviteAcceptToken(inviteUrl: string): string | null {
  try {
    return new URL(inviteUrl, window.location.origin).searchParams.get('token')
  } catch {
    return null
  }
}

function InviteAcceptAction({ inviteUrl }: { inviteUrl: string }) {
  const navigate = useNavigate()
  const { session, isAuthConfigured } = useAuth()
  const { refetchWorkspaces } = useWorkspace()
  const [accepting, setAccepting] = useState(false)
  const token = parseInviteAcceptToken(inviteUrl)

  const handleAccept = async () => {
    if (!token) {
      try {
        const path = new URL(inviteUrl, window.location.origin)
        navigate(`${path.pathname}${path.search}`)
      } catch {
        toast.error('Invalid invite link.')
      }
      return
    }

    if (!isAuthConfigured || !session) {
      navigate(`/login?redirect=${encodeURIComponent(`/invites/accept?token=${token}`)}`)
      return
    }

    setAccepting(true)
    try {
      const res = await fetchWithAuth('/api/invites/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean
        error?: string
        code?: string
      }

      if (res.ok && data.success) {
        await refetchWorkspaces()
        toast.success('Workspace invite accepted.')
        window.dispatchEvent(new CustomEvent('flownotes:check-password-prompt'))
        return
      }

      if (data.code === 'already_used') {
        await refetchWorkspaces()
        toast.info('You are already a member of this workspace.')
        window.dispatchEvent(new CustomEvent('flownotes:check-password-prompt'))
        return
      }

      toast.error(data.error || 'Could not accept invite.')
    } catch {
      toast.error('Could not accept invite.')
    } finally {
      setAccepting(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => void handleAccept()}
      disabled={accepting}
      data-testid={testIds.inbox.inviteAcceptButton}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:opacity-60"
    >
      {accepting ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
      ) : (
        <Link2 className="h-3.5 w-3.5 shrink-0" />
      )}
      Accept workspace invite
    </button>
  )
}

// ---------------------------------------------------------------------------
// Kind config
// ---------------------------------------------------------------------------

const KIND_CONFIG: Record<Kind, { label: string; icon: React.ElementType; color: string }> = {
  voice:      { label: 'Voice',       icon: Mic,           color: 'text-purple-400' },
  email:      { label: 'Email',       icon: Mail,          color: 'text-orange-400' },
  agent:      { label: 'Agent',       icon: Bot,           color: 'text-blue-400' },
  suggestion: { label: 'Suggestions', icon: Zap,           color: 'text-yellow-400' },
  invite:     { label: 'Invite',      icon: Zap,           color: 'text-amber-400' },
  chat:       { label: 'Chats',       icon: MessageSquare, color: 'text-sky-400' },
  flow:       { label: 'Flows',       icon: Zap,           color: 'text-green-400' },
  extension:  { label: 'Extensions',  icon: Building2,     color: 'text-teal-400' },
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InboxReaderProps {
  thread: Thread | null
  onArchive: (id: string) => void
  onSnooze: (id: string, label: string, hint: string) => void
  onUnarchive?: (id: string) => void
  onMarkAsUnread?: (id: string) => void
  onMarkAsRead?: (id: string) => void
  onOpenInChat?: (id: string) => void
  /** Open the thread as its own main-panel tab (meta-click equivalent). */
  onOpenInNewTab?: (id: string) => void
  /** Called when linkedConversationId is missing and needs lazy-linking */
  onHydrate?: (threadId: string) => Promise<string | undefined>
  /** Org name + `#hex` accent for sidebar thread context */
  workspaceAccent?: { label: string; color: string } | null
  /** Refresh inbox list when cloud agent status reaches a terminal state */
  onDispatchStatusUpdated?: () => void
  /** Close the split peek. Required for the h-11 preview header. */
  onClose?: () => void
  /**
   * `standalone` (default): full reader header with title + menu
   *   (InboxPage split pane).
   * `embedded`: chrome lives on InboxThreadToolbar single row; reader keeps
   *   slim source meta only (no duplicate title).
   */
  chrome?: 'standalone' | 'embedded'
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ onClose }: { onClose?: () => void }) {
  if (!onClose) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-8 text-center">
        <MessageSquare className="h-10 w-10 text-muted-foreground/20" />
        <p className="text-sm text-muted-foreground">Select a thread to read</p>
      </div>
    )
  }
  return (
    <IndexPreviewEmpty
      row
      noun="thread"
      label="Inbox"
      onClose={onClose}
    />
  )
}

// ---------------------------------------------------------------------------
// InboxReader
// ---------------------------------------------------------------------------

export function InboxReader({
  thread,
  onArchive,
  onSnooze,
  onUnarchive,
  onMarkAsUnread,
  onMarkAsRead,
  onOpenInChat,
  onOpenInNewTab,
  onHydrate,
  workspaceAccent,
  onDispatchStatusUpdated,
  onClose,
  chrome = 'standalone',
}: InboxReaderProps) {
  const registry = useChatSurfaceRegistryOptional()
  const { needsPassword } = useNeedsPassword()
  const [stepsExpanded, setStepsExpanded] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const embedded = chrome === 'embedded'

  const soulContextDoc = useMemo(
    () => (thread ? getSoulProposalContextDoc(thread) : null),
    [thread],
  )
  const isSoulProposalUnresolved = isUnresolvedSoulProposal(thread)

  const openBoundThreadChat = useCallback(
    async () => {
      if (!thread) return null
      const linked = await openInboxThreadInChat({
        threadId: thread.id,
        title: thread.title,
        linkedConversationId: thread.linkedConversationId,
        hydrate: onHydrate
          ? async (id) => (await onHydrate(id)) ?? null
          : undefined,
        registry,
        forceAgentPanel: true,
        contextDocumentId: soulContextDoc?.documentId,
        contextDocumentTitle: soulContextDoc?.title,
      })
      if (!linked) {
        toast.error('Could not open chat for this thread')
        return null
      }
      return linked
    },
    [thread, onHydrate, registry, soulContextDoc],
  )

  const handleDiscussSoulProposal = useCallback(async () => {
    if (!thread) return
    setIsSending(true)
    try {
      await openBoundThreadChat()
    } finally {
      setIsSending(false)
    }
  }, [thread, openBoundThreadChat])

  const handleAddToCurrentChat = useCallback(() => {
    if (!thread) return
    dispatchInboxThreadAddToChat(thread.id, thread.title)
  }, [thread])

  const handleChromeOpenInChat = useCallback(
    (id: string) => {
      if (isSoulProposalUnresolved || soulContextDoc) {
        void handleDiscussSoulProposal()
        return
      }
      onOpenInChat?.(id)
    },
    [isSoulProposalUnresolved, soulContextDoc, handleDiscussSoulProposal, onOpenInChat],
  )

  const chromeActions = useMemo(() => {
    if (!thread) return null
    return {
      onReply: () => {
        void handleDiscussSoulProposal()
      },
      primaryActionLabel: isSoulProposalUnresolved ? ('Discuss' as const) : undefined,
      threadId: thread.id,
      threadTitle: thread.title,
      threadStatus: thread.status,
      linkedConversationId: thread.linkedConversationId ?? null,
      onSnooze,
      onArchive,
      onMarkAsUnread,
      onMarkAsRead,
      onOpenInChat: handleChromeOpenInChat,
      onOpenInNewTab,
      showThreadActions: thread.status !== 'archived',
    }
  }, [
    thread,
    isSoulProposalUnresolved,
    handleDiscussSoulProposal,
    onSnooze,
    onArchive,
    onMarkAsUnread,
    onMarkAsRead,
    handleChromeOpenInChat,
    onOpenInNewTab,
  ])

  useRegisterInboxThreadChromeActions(chromeActions, embedded && Boolean(thread))

  // Must run before any early return - selecting a thread used to add this hook mid-tree.
  const stableThreadId = thread?.id ?? null
  const { messages: threadMessages, refetch: refetchThreadMessages } =
    useThreadMessages(stableThreadId)

  /** agent_chat content lives in ai_messages via linked_conversation_id, not thread_messages. */
  const [chatPreviewMessages, setChatPreviewMessages] = useState<AIMessage[]>([])
  const [chatPreviewLoading, setChatPreviewLoading] = useState(false)

  useEffect(() => {
    if (!thread || thread.kind !== 'chat') {
      setChatPreviewMessages([])
      setChatPreviewLoading(false)
      return
    }
    let cancelled = false
    const load = async () => {
      setChatPreviewLoading(true)
      try {
        let convId = thread.linkedConversationId ?? null
        if (!convId && onHydrate) {
          convId = (await onHydrate(thread.id)) ?? null
        }
        if (!convId) {
          const t = await getThread(thread.id)
          convId =
            t.linkedConversationId
            ?? (typeof t.metadata?.linked_conversation_id === 'string'
              ? t.metadata.linked_conversation_id
              : null)
        }
        if (!convId || cancelled) {
          if (!cancelled) setChatPreviewMessages([])
          return
        }
        const result = await getConversation(convId, true)
        if (cancelled) return
        const msgs = (result.conversation.messages ?? []).filter((m) => m.role !== 'system')
        setChatPreviewMessages(msgs.slice(-16))
      } catch (err) {
        console.error('[InboxReader] Failed to load chat preview:', err)
        if (!cancelled) setChatPreviewMessages([])
      } finally {
        if (!cancelled) setChatPreviewLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [thread?.id, thread?.kind, thread?.linkedConversationId, onHydrate])

  const openSoulDocument = useCallback((documentIdOrPath: string) => {
    const target = String(documentIdOrPath || '').trim()
    if (!target) return
    window.dispatchEvent(
      new CustomEvent('open-document', {
        detail: target.includes('/')
          ? { filePath: target, documentId: target }
          : { documentId: target, filePath: target },
      }),
    )
  }, [])

  const isSoulThread = isSoulProposalThread(thread)
  const proposalMessages = useMemo(() => {
    if (!isSoulThread) return [] as ThreadMessage[]
    return threadMessages.filter(
      (m) => (m.metadata as { kind?: string } | undefined)?.kind !== 'soul_proposal_decision',
    )
  }, [isSoulThread, threadMessages])
  const decisionMessages = useMemo(() => {
    if (!isSoulThread) return [] as ThreadMessage[]
    return threadMessages.filter(
      (m) => (m.metadata as { kind?: string } | undefined)?.kind === 'soul_proposal_decision',
    )
  }, [isSoulThread, threadMessages])

  if (!thread || !stableThreadId) {
    return (
      <div data-testid={testIds.inbox.reader} className="flex h-full min-w-0 flex-col overflow-hidden">
        <EmptyState onClose={onClose} />
      </div>
    )
  }

  // Locals after null guard - safe to close over in nested handlers.
  const activeThread = thread
  const threadId = stableThreadId
  const cfg = KIND_CONFIG[activeThread.kind]
  const Icon = cfg.icon
  const isArchived = activeThread.status === 'archived'
  const isSnoozed = activeThread.status === 'snoozed'
  const linkedConversationId = activeThread.linkedConversationId ?? null

  const handleDispatchStatusUpdated = () => {
    void refetchThreadMessages()
    onDispatchStatusUpdated?.()
  }

  const latestThreadBody =
    threadMessages.length > 0 ? threadMessages[threadMessages.length - 1]?.body ?? '' : ''
  /** Prefer proposal body (not the latest decision event) for single-card fallbacks. */
  const proposalBody =
    proposalMessages.length > 0
      ? proposalMessages[proposalMessages.length - 1]?.body ?? ''
      : ''
  const readerBody =
    (isSoulThread ? proposalBody : latestThreadBody)
    || activeThread.body
    || activeThread.preview

  async function openThreadInChat() {
    // Soul proposals always bind + attach target soul doc chip (never reuse foreign pane).
    if (isSoulProposalUnresolved || soulContextDoc) {
      await openBoundThreadChat()
      return
    }
    if (onOpenInChat) {
      onOpenInChat(threadId)
      return
    }
    await openBoundThreadChat()
  }

  const canOpenChatFooter = Boolean(onOpenInChat || linkedConversationId || thread.kind === 'chat')

  return (
    <div
      data-testid={testIds.inbox.reader}
      className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
    >
      {embedded ? null : (
        <PreviewPaneHeader
          row
          testId={testIds.inbox.readerHeader}
          icon={<Icon className={cn('h-4 w-4', cfg.color)} />}
          chips={
            <>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {activeThread.title}
              </span>
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {cfg.label}
              </span>
              {workspaceAccent?.label?.trim() ? (
                <WorkspaceAccentLabel
                  label={workspaceAccent.label}
                  colorHex={workspaceAccent.color}
                  size="reader"
                />
              ) : null}
            </>
          }
          actions={
            !isArchived ? (
              <ThreadActionsMenu
                compact
                threadId={activeThread.id}
                threadTitle={activeThread.title}
                threadStatus={activeThread.status}
                linkedConversationId={activeThread.linkedConversationId ?? null}
                surface="reader"
                onSnooze={onSnooze}
                onArchive={onArchive}
                onMarkAsUnread={onMarkAsUnread}
                onMarkAsRead={onMarkAsRead}
                onOpenInChat={handleChromeOpenInChat}
                onOpenInNewTab={onOpenInNewTab}
              />
            ) : null
          }
          onOpen={
            onOpenInNewTab
              ? () => onOpenInNewTab(activeThread.id)
              : undefined
          }
          openTitle="Open in tab"
          onClose={onClose ?? (() => undefined)}
        />
      )}

      {/* Archived banner */}
      {isArchived && (
        <div className="flex items-center gap-2 px-4 py-2 bg-muted/30 border-b border-border shrink-0">
          <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground flex-1">This thread is archived</span>
          {onUnarchive && (
            <button
              onClick={() => onUnarchive(thread.id)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <RotateCcw className="h-3 w-3" /> Move to inbox
            </button>
          )}
        </div>
      )}

      {/* Snoozed banner */}
      {isSnoozed && thread.snoozedUntil && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 shrink-0">
          <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          <span className="text-xs text-amber-600 flex-1">Snoozed until {thread.snoozedUntil}</span>
          {onUnarchive && (
            <button
              onClick={() => onUnarchive(thread.id)}
              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 transition-colors"
            >
              <RotateCcw className="h-3 w-3" /> Wake up now
            </button>
          )}
        </div>
      )}

      {/* Header: standalone = full title + actions; embedded = slim meta (toolbar owns title/actions) */}
      {embedded ? (
        <div
          className="flex items-center gap-2 shrink-0 border-b border-border px-4 py-2"
          data-testid={testIds.inbox.readerMeta}
        >
          <div className={cn('shrink-0', cfg.color)}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="truncate text-xs text-muted-foreground">{thread.source}</span>
          {workspaceAccent?.label?.trim() ? (
            <WorkspaceAccentLabel
              label={workspaceAccent.label}
              colorHex={workspaceAccent.color}
              size="reader"
            />
          ) : null}
        </div>
      ) : null}

      {/* Linked docs */}
      {thread.linkedDocs && thread.linkedDocs.length > 0 && (
        <div className="shrink-0 border-b border-border bg-muted/20 px-4 py-2">
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground/60">Linked docs</p>
          {thread.linkedDocs.map(d => (
            <span key={d} className="block truncate font-mono text-sm text-muted-foreground">
              {d}
            </span>
          ))}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
        {thread.kind === 'agent' ? (
          <>
            <AgentDispatchPanel
              meta={(thread.metadata ?? {}) as import('./agent-dispatch-panel').AgentDispatchMeta}
              onStatusUpdated={handleDispatchStatusUpdated}
            />
            {threadMessages.length > 0 ? (
              <div
                className="space-y-3 border-t border-border px-4 py-4"
                data-testid={testIds.inbox.agentThreadMessages}
              >
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/60">
                  Thread
                </p>
                {threadMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="rounded border border-border bg-muted/30 px-3 py-2"
                  >
                    <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {msg.source === 'agent' ? 'Cloud agent' : msg.source}
                    </p>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                      {msg.body}
                    </p>
                  </div>
                ))}
              </div>
            ) : thread.body ? (
              <div className="whitespace-pre-wrap border-t border-border px-4 pt-4 pb-4 text-sm leading-relaxed text-muted-foreground">
                {thread.body}
              </div>
            ) : null}
          </>
        ) : (
          <div className="px-4 py-4 space-y-4">
            {thread.kind === 'flow' && thread.flowSteps ? (
          <>
            {/* Flow summary */}
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-green-500/20 text-green-400">
                <Zap className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-green-400">{thread.flowName ?? 'Flow'}</span>
                  <span className="text-xs text-muted-foreground">{thread.age}</span>
                </div>
                <div className="rounded border border-border bg-muted/40 p-4">
                  <InboxMarkdownBody body={readerBody} />
                </div>
              </div>
            </div>

            {/* Step log */}
            <div className="rounded border border-border overflow-hidden">
              <button
                onClick={() => setStepsExpanded(v => !v)}
                className="flex items-center gap-2 w-full px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              >
                {stepsExpanded
                  ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                  : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
                <span className="flex-1 text-sm font-medium text-foreground">
                  Execution steps ({thread.flowSteps.length})
                </span>
                <span className="text-xs text-muted-foreground">
                  {thread.flowSteps.filter(s => s.status === 'done').length}/{thread.flowSteps.length} done
                  {thread.flowSteps.some(s => s.status === 'error') && (
                    <span className="ml-1 text-red-400">1 error</span>
                  )}
                </span>
              </button>

              {stepsExpanded && (
                <div className="divide-y divide-border">
                  {thread.flowSteps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5 px-3 py-2">
                      <span className={cn(
                        'shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold',
                        step.status === 'done'    && 'bg-green-500/20 text-green-400',
                        step.status === 'skipped' && 'bg-muted text-muted-foreground',
                        step.status === 'error'   && 'bg-red-500/20 text-red-400',
                      )}>
                        {step.status === 'done' ? '?' : step.status === 'error' ? '!' : '-'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm leading-snug text-foreground">{step.label}</p>
                        {step.detail && (
                          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{step.detail}</p>
                        )}
                        {step.outputDoc && (
                          <span className="mt-1 inline-flex items-center gap-1 font-mono text-xs text-primary/70">
                            <FileText className="h-2.5 w-2.5 shrink-0" />
                            {step.outputDoc.split('/').pop()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button className="flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                <Zap className="h-3.5 w-3.5" /> View flow
              </button>
              <button className="flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                <RefreshCw className="h-3.5 w-3.5" /> Re-run
              </button>
            </div>
          </>
        ) : thread.kind === 'voice' ? (
          <>
            <div className="flex gap-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-purple-500/20 text-[11px] font-bold text-purple-400">
                Y
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-purple-400">You - voice</span>
                  <span className="text-xs text-muted-foreground">{thread.age}</span>
                </div>
                <div className="space-y-2 rounded border border-border bg-muted/40 p-4">
                  <div className="flex items-center gap-2">
                    <button type="button" className="shrink-0 text-primary">
                      <Play className="h-4 w-4 fill-current" />
                    </button>
                    <div className="h-1 flex-1 rounded-full bg-border">
                      <div className="h-1 w-0 rounded-full bg-primary" />
                    </div>
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">
                      {thread.audioDuration ?? '--:--'}
                    </span>
                  </div>
                  {thread.audioFilename && (
                    <p className="flex items-center gap-1 truncate font-mono text-xs text-muted-foreground/60">
                      <Mic className="h-3.5 w-3.5 shrink-0" />
                      {thread.audioFilename}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    "{thread.preview.replace(/^"|"$/g, '')}"
                  </p>
                  <p className="text-xs italic text-muted-foreground/50">Transcript - auto-generated</p>
                </div>
              </div>
            </div>
          </>
        ) : thread.kind === 'chat' ? (
          <div className="space-y-3" data-testid={testIds.inbox.chatPreview}>
            {chatPreviewLoading && chatPreviewMessages.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading chat…
              </div>
            ) : chatPreviewMessages.length === 0 ? (
              <div className="rounded border border-border bg-muted/40 p-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  No message preview available for this chat yet.
                </p>
                {canOpenChatFooter ? (
                  <button
                    type="button"
                    onClick={() => void openThreadInChat()}
                    className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <OpenDestinationIcons.openInChat className="h-3.5 w-3.5" /> Open in chat
                  </button>
                ) : null}
              </div>
            ) : (
              chatPreviewMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    'rounded border border-border px-3 py-2',
                    msg.role === 'assistant' ? 'bg-muted/40' : 'bg-background',
                  )}
                >
                  <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {msg.role === 'assistant' ? 'Agent' : msg.role === 'user' ? 'You' : msg.role}
                  </p>
                  <div className="text-sm leading-relaxed text-muted-foreground">
                    <InboxMarkdownBody body={msg.content || ''} />
                  </div>
                </div>
              ))
            )}
          </div>
        ) : isSoulThread ? (
          <div
            className="flex gap-4"
            data-testid={testIds.inbox.soulProposalTimeline}
          >
            {workspaceAccent?.label ? (
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-[12px] font-bold text-white shadow-sm ring-1 ring-border/60"
                style={{ backgroundColor: workspaceAccent.color }}
              >
                {workspaceInitials(workspaceAccent.label)}
              </div>
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-muted text-[12px] font-bold text-muted-foreground ring-1 ring-border/40">
                {(thread.source[0] ?? 'X').toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{thread.source}</span>
                <span className="text-xs text-muted-foreground">{thread.age}</span>
              </div>
              {soulContextDoc ? (
                <button
                  type="button"
                  onClick={() => openSoulDocument(soulContextDoc.documentId)}
                  data-testid={testIds.inbox.soulProposalDocLink}
                  className="inline-flex max-w-full items-center gap-1.5 rounded border border-border bg-muted/30 px-2 py-1 text-xs font-medium text-primary hover:bg-muted"
                  title={
                    typeof activeThread.metadata?.target_path === 'string'
                      ? activeThread.metadata.target_path
                      : soulContextDoc.title
                  }
                >
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{soulContextDoc.title}</span>
                </button>
              ) : null}
              {(proposalMessages.length > 0
                ? proposalMessages
                : readerBody
                  ? [{ id: 'fallback', body: readerBody } as ThreadMessage]
                  : []
              ).map((msg) => (
                <div
                  key={msg.id}
                  className="rounded border border-border bg-muted/40 p-4"
                >
                  <InboxMarkdownBody body={msg.body || ''} />
                </div>
              ))}
              {decisionMessages.map((msg) => {
                const meta = (msg.metadata ?? {}) as {
                  decision?: string
                  event?: string
                  actor_label?: string
                  target_document_id?: string
                  target_path?: string
                }
                const decision =
                  meta.decision === 'dismissed' || meta.event === 'dismissed'
                    ? 'dismissed'
                    : 'approved'
                const path =
                  typeof meta.target_path === 'string'
                    ? meta.target_path
                    : typeof activeThread.metadata?.target_path === 'string'
                      ? activeThread.metadata.target_path
                      : ''
                const docId =
                  (typeof meta.target_document_id === 'string'
                    ? meta.target_document_id.trim()
                    : '')
                  || soulContextDoc?.documentId
                  || path
                const filename = path.split('/').filter(Boolean).pop() || soulContextDoc?.title || 'Document'
                return (
                  <SoulProposalDecisionEvent
                    key={msg.id}
                    decision={decision}
                    actorLabel={
                      typeof meta.actor_label === 'string' && meta.actor_label
                        ? meta.actor_label
                        : 'Someone'
                    }
                    at={msg.createdAt}
                    documentId={docId}
                    filename={filename}
                    targetPath={path}
                    onOpenDocument={openSoulDocument}
                  />
                )
              })}
              {/* Legacy resolved threads (approved before decision messages existed) */}
              {decisionMessages.length === 0
                && activeThread.metadata?.resolved === true
                && soulContextDoc ? (
                <SoulProposalDecisionEvent
                  decision={
                    activeThread.metadata?.dismissed === true ? 'dismissed' : 'approved'
                  }
                  actorLabel={
                    typeof activeThread.metadata?.approved_by_label === 'string'
                      ? activeThread.metadata.approved_by_label
                      : typeof activeThread.metadata?.dismissed_by_label === 'string'
                        ? activeThread.metadata.dismissed_by_label
                        : 'Someone'
                  }
                  at={
                    (typeof activeThread.metadata?.approved_at === 'string'
                      && activeThread.metadata.approved_at)
                    || (typeof activeThread.metadata?.dismissed_at === 'string'
                      && activeThread.metadata.dismissed_at)
                    || activeThread.receivedAt
                  }
                  documentId={soulContextDoc.documentId}
                  filename={soulContextDoc.title}
                  targetPath={
                    typeof activeThread.metadata?.target_path === 'string'
                      ? activeThread.metadata.target_path
                      : ''
                  }
                  onOpenDocument={openSoulDocument}
                />
              ) : null}
              {isSoulProposalUnresolved ? (
                <SoulProposalActionBar
                  thread={activeThread}
                  variant="inline"
                  onResolved={handleDispatchStatusUpdated}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex gap-4">
            {workspaceAccent?.label ? (
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-[12px] font-bold text-white shadow-sm ring-1 ring-border/60"
                style={{ backgroundColor: workspaceAccent.color }}
              >
                {workspaceInitials(workspaceAccent.label)}
              </div>
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-muted text-[12px] font-bold text-muted-foreground ring-1 ring-border/40">
                {(thread.source[0] ?? 'X').toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{thread.source}</span>
                <span className="text-xs text-muted-foreground">{thread.age}</span>
              </div>
              <div className="space-y-3">
                {(
                  threadMessages.filter((msg) => !isDocumentEventMessage(msg)).length > 0
                    ? threadMessages.filter((msg) => !isDocumentEventMessage(msg))
                    : readerBody
                      ? [{ id: 'fallback', body: readerBody } as ThreadMessage]
                      : []
                ).map((msg) => (
                  <div
                    key={msg.id}
                    className="rounded border border-border bg-muted/40 p-4"
                  >
                    <InboxMarkdownBody body={msg.body || ''} />
                    {typeof thread.metadata?.invite_url === 'string' && thread.metadata.invite_url.length > 0 && (
                      <div className="mt-4 space-y-2 border-t border-border pt-4">
                        <InviteAcceptAction inviteUrl={thread.metadata.invite_url} />
                        {needsPassword && (
                          <p className="text-xs leading-relaxed text-muted-foreground">
                            Next step:{' '}
                            <button
                              type="button"
                              className="font-medium text-primary hover:underline"
                              onClick={() => openAccountPasswordSettings()}
                            >
                              set your password
                            </button>{' '}
                            in Account settings so you can sign in again later.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {threadMessages.filter(isDocumentEventMessage).map((msg) => {
                  const meta = (msg.metadata ?? {}) as {
                    event?: string
                    actor_label?: string
                    title?: string
                    document_id?: string
                    old_path?: string
                    new_path?: string
                    documents?: Array<{ id?: string; title?: string; path?: string }>
                  }
                  const event =
                    meta.event === 'moved' || meta.event === 'created_batch' || meta.event === 'created'
                      ? meta.event
                      : 'created'
                  return (
                    <InboxDocumentEvent
                      key={msg.id}
                      event={event}
                      actorLabel={
                        typeof meta.actor_label === 'string' && meta.actor_label
                          ? meta.actor_label
                          : 'Someone'
                      }
                      at={msg.createdAt}
                      title={typeof meta.title === 'string' && meta.title ? meta.title : thread.title}
                      documentId={typeof meta.document_id === 'string' ? meta.document_id : null}
                      oldPath={typeof meta.old_path === 'string' ? meta.old_path : null}
                      newPath={typeof meta.new_path === 'string' ? meta.new_path : null}
                      documents={Array.isArray(meta.documents) ? meta.documents : undefined}
                      onOpenDocument={openSoulDocument}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        )}
          </div>
        )}
        </div>

      {/* Soul proposals: Discuss escape hatch (Approve lives under the card). */}
      {!isArchived && isSoulProposalUnresolved && (
        <div
          className="shrink-0 space-y-2 border-t border-border bg-background px-4 pb-5 pt-3"
          data-testid={testIds.inbox.soulProposalFooter}
        >
          <p className="text-xs text-muted-foreground">
            Approve applies the patch here. Discuss opens a chat about this proposal.
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => void handleDiscussSoulProposal()}
              disabled={isSending}
              data-testid={testIds.inbox.soulProposalDiscuss}
              className="flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {isSending ? 'Opening…' : 'Discuss in chat'}
            </button>
          </div>
        </div>
      )}

      {!isArchived && !isSoulProposalUnresolved && (
        <div className="shrink-0 flex items-center justify-end gap-2 border-t border-border bg-background px-4 py-3">
          <button
            type="button"
            onClick={handleAddToCurrentChat}
            data-testid={testIds.inbox.readerAddToChat}
            className="flex items-center gap-1.5 rounded border border-border px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <AddToCurrentChatIcon className="h-3.5 w-3.5" /> {ADD_TO_CURRENT_CHAT_LABEL}
          </button>
          {canOpenChatFooter && (
            <button
              type="button"
              onClick={() => void openThreadInChat()}
              className="flex items-center gap-1.5 rounded border border-border px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <OpenDestinationIcons.openInChat className="h-3.5 w-3.5" /> Open in chat
            </button>
          )}
        </div>
      )}
      </div>
    </div>
  )
}
